/**
 * The Monday garden email: how often a gardener gets it (weekly, monthly on
 * the first Monday, or off), from their profile, and from the links in the
 * email itself, which need no sign-in (the token, sent only to them, is
 * what the backend trusts; routes/gardenEmail). Warning emails are not
 * here: they have their own switch (api/alerts).
 *
 * Endpoints (every answer also carries `success`):
 * - GET /garden-email/preference → MondayEmailPreference (signed in).
 * - PUT /garden-email/preference { frequency } → MondayEmailPreference.
 * - GET /garden-email/link?t=<token> → MondayEmailLink (no sign-in).
 * - POST /garden-email/link { token, frequency } → MondayEmailLink (no sign-in).
 *
 * Who uses it: user/UserProfile, user/components/MondayEmailScreen and
 * features/email/MondayEmailLinkPage. Caching: the preference under
 * MONDAY_EMAIL_KEY (['gardenEmail', 'preference'], profileQueries); the
 * link page asks once and keeps nothing.
 *
 * @module api/gardenEmail
 */

import { apiClient } from './apiClient';

/** How often the Monday garden email comes. */
export type MondayFrequency = 'weekly' | 'monthly' | 'off';

/** Every choice, in the order they are offered. */
export const MONDAY_FREQUENCIES: readonly MondayFrequency[] = ['weekly', 'monthly', 'off'];

/** The gardener's choice. */
export interface MondayEmailPreference {
  /** How often it comes. */
  frequency: MondayFrequency;
}

/** What a link from the email would change: unknown (no such link) or ready, with the choice now. */
export interface MondayEmailLink {
  /** unknown: no such link; ready: it can change the choice. */
  status: 'unknown' | 'ready';
  /** The choice now; null for an unknown link. */
  frequency: MondayFrequency | null;
}

/**
 * Whether a value is one of the choices.
 *
 * @param value - From an address or the server.
 * @returns True for weekly, monthly or off.
 */
export function isMondayFrequency(value: unknown): value is MondayFrequency {
  return typeof value === 'string' && (MONDAY_FREQUENCIES as readonly string[]).includes(value);
}

/**
 * The signed-in gardener's choice.
 *
 * @returns Their choice (weekly until they change it).
 */
export async function getMondayEmail(): Promise<MondayEmailPreference> {
  const response = await apiClient.get<MondayEmailPreference & { success: boolean }>('/garden-email/preference');
  return { frequency: response.data.frequency };
}

/**
 * Sets the signed-in gardener's choice.
 *
 * @param frequency - Weekly, monthly or off.
 * @returns The choice as kept.
 */
export async function setMondayEmail(frequency: MondayFrequency): Promise<MondayEmailPreference> {
  const response = await apiClient.put<MondayEmailPreference & { success: boolean }>('/garden-email/preference', { frequency });
  return { frequency: response.data.frequency };
}

/**
 * Asks what a link from the email would change. Needs no sign-in.
 *
 * @param token - From the link.
 * @returns The link's state.
 */
export async function readMondayLink(token: string): Promise<MondayEmailLink> {
  const response = await apiClient.get<MondayEmailLink & { success: boolean }>('/garden-email/link', { params: { t: token } });
  return { status: response.data.status, frequency: response.data.frequency };
}

/**
 * The page's one button: sets the choice of the gardener the link was sent to. Needs no sign-in.
 *
 * @param token - From the link.
 * @param frequency - What the link asked for.
 * @returns The link's state after.
 */
export async function confirmMondayLink(token: string, frequency: MondayFrequency): Promise<MondayEmailLink> {
  const response = await apiClient.post<MondayEmailLink & { success: boolean }>('/garden-email/link', { token, frequency });
  return { status: response.data.status, frequency: response.data.frequency };
}
